// @ts-check
// File: app/src/utils/workoutCompiler.js
//
// Compiles a nested workoutPlan tree into a 1D execution queue for the
// WorkoutEngine. Each queue item carries sourceTreePath + loopCounters
// metadata so the UI can continue rendering the original tree unchanged.
//
// The compiler also applies the adjacent-recovery rest filter (gated by
// skipLastRest), which removes recovery phases that are immediately
// followed by another recovery phase or the end of the session.
//
// Pure module — zero React imports, zero side effects, never mutates
// the input tree.

import { toSeconds } from "./time";
import { estimatePerCalSec, estimatePerRepSec } from "./repEstimator";
import { isCalorieUnit, isCountedUnit, isTimedUnit } from "./planNodeUtils";
import {
  isTransitionNode,
  isInjectedChangeover,
  isHiddenInSummary,
  isProtocolWindowGroup,
  resolveSetScheme,
} from "./planVisuals";

/** @typedef {import("../types/plan").PlanNode} PlanNode */
/** @typedef {import("../types/plan").QueueItem} QueueItem */
/** @typedef {import("../types/plan").CompiledPlan} CompiledPlan */

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

// A node is "recovery" if its type name contains "rest", or is "transition"
// or "cooldown". Everything else ("Work", "Warmup", custom active types) is
// "active". `cooldown` is classified as recovery so the engine skips the
// count-in into one (you transition into a cooldown, you don't count down to
// it). It is NOT subject to the trailing/adjacent rest filter, however —
// applyRestFilter exempts cooldown because it's an explicit, authored phase of
// the session that must always execute (see issue #19). The cue layer keys
// cooldown off its own type, so recovery classification doesn't pull cooldown
// into the rest/set_rest cue.
const classifyNode = (node) => {
  const type = String(node?.type || "").toLowerCase();
  if (type.includes("rest") || type === "transition" || type === "cooldown") {
    return "recovery";
  }
  return "active";
};

const isTimedNode = (node) => isTimedUnit(node?.unit);

// A transition node is logistical changeover time (move between stations / set
// up the next block). Per issue #22 it is a TIME PREDICTION only: it keeps its
// durationSec in the projection (totalDuration / calculateSmartRemaining) but
// the engine never lands on it (executable:false) — phases advance straight
// through so the user never sees a transition during the session. See
// isTransitionNode in planVisuals.js.

// Composite fingerprint of every active loop iteration, used by the engine
// for skipRoundForward / skipRoundBack. A change in ANY loop counter yields
// a different fingerprint, so skipRoundForward naturally targets the
// innermost repeating boundary.
const buildRoundKey = (loopCounters) => {
  const entries = Object.entries(loopCounters);
  if (entries.length === 0) return null;
  return entries.map(([id, iter]) => `${id}:${iter}`).join("|");
};

// Key a Group's loop counter by its own id, falling back to its position in the
// tree when it has none. The fallback matters: `tracksRounds` used to require a
// non-null id, so an unidentified sets-Group tracked no iteration at all, every
// descendant resolved its `setScheme` at set 1, and a ramped block ran "8 reps"
// for all four sets (issue #74). The `@` prefix cannot collide with a real id,
// and the key is stable across recompiles of the same tree — which is what
// updateConfig/restoreState match positions on.
//
// Exported because anything reading a queue item's / wire block's loopCounters
// has to key them the same way. Reading `loopCounters[node.id]` directly is the
// bug this fallback exists to prevent — see resolveBlockNode in liveTimeline.js.
export const loopKeyFor = (node, pathStack) =>
  node.id == null || node.id === "" ? `@${pathStack.join(".")}` : node.id;

// Per-set parameter resolution (`setScheme`, issue #29) lives in planVisuals as
// `resolveSetScheme` — shared with the plan/outline renderers so the figures the
// queue executes and the ones the UI displays can never drift (issue #42).

// ---------------------------------------------------------------------------
// Flattening
// ---------------------------------------------------------------------------

// Recursively walks the plan tree and pushes leaf items onto `out`.
// Mutates `pathStack` and `loopCounters` in place for efficiency, but
// always restores them before returning (classic DFS backtracking).
//
// `enclosingGroup` is the nearest multi-round Group ancestor and is used by
// `applyRestFilter` to identify the last item of the last iteration of that
// group (so a trailing round-boundary rest can be stripped, see filter).
const walk = (node, pathStack, loopCounters, ctx, out, enclosingGroup) => {
  if (!node) return;

  // Time-capped container (AMRAP / "for time" cap, issue #36): a Group whose
  // unit is a time unit (s/m). Its `value` is the seconds/minutes CAP, NOT an
  // iteration count, so we emit ONE executable timed item equal to the cap and
  // do NOT recurse into the children — they are a checklist of movements to
  // cycle through, not individual timed phases. This mirrors the firmware/back-
  // end contract (translatePlan.js maps such a Group to loop_mode:"timed").
  if (node.type === "Group" && isTimedNode(node)) {
    const roundKey = buildRoundKey(loopCounters);
    out.push({
      node, // the Group itself — carries label + children (the checklist)
      instanceId: `${node?.id ?? "n"}#${roundKey ?? "0"}`,
      sourceTreePath: Object.freeze([...pathStack]),
      loopCounters: { ...loopCounters },
      kind: classifyNode(node),
      isTimed: true,
      isEstimated: false,
      estimateSource: null,
      resolvedLoadPct: null,
      durationSec: toSeconds(node.value, node.unit),
      roundKey,
      enclosingGroup,
      isTransition: false,
      executable: true,
    });
    return;
  }

  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  // Leaf node — emit a queue item.
  if (!hasChildren) {
    // Per-set parameter resolution (issue #29). When a Work carries a
    // `setScheme`, the active set is the enclosing sets-Group's iteration; emit
    // a node whose value/weight/intensityPct reflect that set. `emitNode` is the
    // original node (reference preserved) when there is nothing to vary.
    const setIdx = enclosingGroup ? enclosingGroup.iter : 0;
    const emitNode = resolveSetScheme(node, setIdx);

    const timed = isTimedNode(emitNode);
    const transition = isTransitionNode(emitNode);
    let durationSec = 0;
    let estimateSource = null;
    let resolvedLoadPct = null;

    if (timed) {
      durationSec = toSeconds(emitNode.value, emitNode.unit);
    } else if (isCountedUnit(emitNode.unit)) {
      // Counted work (reps or calories) is self-paced — the phase ends on the
      // user's Next press. The duration here is an ESTIMATE, carried purely so
      // the phase contributes its real share to the session total, the timeline
      // bar and calculateSmartRemaining. Without it a 100 cal bike sprint would
      // project as 0:00.
      const count = Number(emitNode.value);
      const cal = isCalorieUnit(emitNode.unit);
      const est = cal
        ? estimatePerCalSec(emitNode, ctx.trainingData, ctx.historyMap)
        : estimatePerRepSec(emitNode, ctx.trainingData, ctx.historyMap);
      const perUnitSec = "perCalSec" in est ? est.perCalSec : est.perRepSec;
      durationSec =
        Number.isFinite(count) && count > 0 ? perUnitSec * count : 0;
      estimateSource = est.source;
      resolvedLoadPct = est.loadPct;
    }

    const roundKey = buildRoundKey(loopCounters);
    out.push({
      node: emitNode, // per-set resolved node (or original when unvaried)
      // Deterministic per-instance key: unique across both sibling leaves
      // (differ by node id) and repeated rounds of one leaf (differ by
      // roundKey). Disambiguates identical node ids without trusting the loop
      // counter alone. See issue #29.
      instanceId: `${node?.id ?? "n"}#${roundKey ?? "0"}`,
      sourceTreePath: Object.freeze([...pathStack]),
      loopCounters: { ...loopCounters },
      kind: classifyNode(emitNode),
      isTimed: timed,
      isEstimated: !timed && isCountedUnit(emitNode.unit),
      estimateSource,
      resolvedLoadPct,
      durationSec,
      roundKey,
      enclosingGroup, // null when not inside a multi-round group
      // Transitions stay in the queue (so durationSec still counts toward the
      // projected total/remaining) but are flagged non-executable so the engine
      // skips over them — see WorkoutEngine._seekExecutable. Issue #22.
      isTransition: transition,
      executable: !transition,
    });
    return;
  }

  // Group node — iterate `value` times (default 1), updating loopCounters
  // for each iteration so descendant leaves snapshot the correct state.
  // Only push to loopCounters when iterations > 1 — otherwise a single
  // iteration root group ("root", value=1) would pollute every queue item's
  // roundKey with a no-op counter.
  const isGroup = node.type === "Group";
  const iterations = isGroup && node.value > 0 ? node.value : 1;
  const groupId = isGroup ? loopKeyFor(node, pathStack) : null;
  const tracksRounds = isGroup && iterations > 1;

  for (let iter = 0; iter < iterations; iter++) {
    if (tracksRounds) loopCounters[groupId] = iter;
    const childEnclosing = tracksRounds
      ? {
          id: groupId,
          iterations,
          iter,
          childCount: node.children.length,
          // Fixed-window protocol (EMOM family): its trailing rest completes
          // the final window and survives the round-boundary strip below.
          isProtocolWindow: isProtocolWindowGroup(node),
        }
      : enclosingGroup;

    for (let i = 0; i < node.children.length; i++) {
      pathStack.push("children", i);
      const isLastChild = i === node.children.length - 1;
      const enclosingForChild = tracksRounds
        ? { ...childEnclosing, isLastChild }
        : enclosingGroup;
      walk(
        node.children[i],
        pathStack,
        loopCounters,
        ctx,
        out,
        enclosingForChild,
      );
      pathStack.pop();
      pathStack.pop();
    }
  }

  if (tracksRounds) delete loopCounters[groupId];
};

// ---------------------------------------------------------------------------
// Rest filter (adjacent-recovery rule)
// ---------------------------------------------------------------------------

const SUMMARY_TRANSITION_HIDE_SECONDS = 60;

// Either flavour of injected changeover node (time-only transition OR Setup
// section). Used by the round-rest filter to avoid mistaking an injected
// changeover for a strippable round-boundary rest.
const isTransitionItem = (item) => isInjectedChangeover(item?.node);

// A cooldown is an explicit, authored wind-down phase. It is classified as
// "recovery" (so the engine never counts in to it) but must NEVER be stripped
// by the rest filter, even at end-of-session. See issue #19.
const isCooldownItem = (item) =>
  String(item?.node?.type || "").toLowerCase() === "cooldown";

// The residual rest of an EMOM / E2MOM / E3MOM… window: prescribed recovery
// that completes the block's final window, not an incidental round-boundary or
// stacked rest. Dropping it makes a "12-Min EMOM" compile to 11:36, or to
// 10:53 when a cool-down follows (issue #63). Injected changeovers inside the
// block are not residuals and keep the normal rules. See isProtocolWindowGroup
// in planVisuals.js.
const isWindowResidualItem = (item) =>
  !!item?.enclosingGroup?.isProtocolWindow && !isTransitionItem(item);

// Strip a recovery item when:
//   • the next item is also recovery (adjacent-recovery collapse), or
//   • it's the last item of the queue (trailing session rest), or
//   • it's the last child of the last iteration of an enclosing multi-round
//     Group AND it's a short, unlabelled round-boundary rest.
// The third rule restores the user's intuition that the last round needs no
// trailing rest, while preserving injected changeover nodes (transitions and
// Setup sections, via isTransitionItem) which are never round-boundary rests.
// A cooldown is exempt from all three rules (see isCooldownItem / issue #19):
// it's an explicit session phase and always executes. An EMOM's residual rest
// is exempt from the first and third — it completes the block's final window,
// so stripping it makes the block shorter than its own label (issue #63). It
// stays subject to the second: a session still never ends on a rest.
const applyRestFilter = (queue, skipLastRest) => {
  if (!skipLastRest) return queue;
  return queue.filter((item, i, arr) => {
    if (item.kind !== "recovery") return true;
    if (isCooldownItem(item)) return true; // explicit cooldown — never stripped
    const next = arr[i + 1];
    if (!next) return false; // end-of-session trailing rest
    if (next.kind === "recovery" && !isWindowResidualItem(item)) {
      return false; // adjacent recoveries
    }
    const enc = item.enclosingGroup;
    if (
      enc &&
      !isWindowResidualItem(item) && // EMOM's last window is rested out in full
      enc.isLastChild &&
      enc.iter === enc.iterations - 1 &&
      item.durationSec < SUMMARY_TRANSITION_HIDE_SECONDS &&
      !isTransitionItem(item)
    ) {
      return false; // last-iteration trailing round-rest
    }
    return true;
  });
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const buildCtx = ({ profile, historyMap }) => ({
  trainingData: profile?.training_data || null,
  historyMap: historyMap || null,
});

const sumDurations = (items) =>
  items.reduce((sum, item) => sum + item.durationSec, 0);

/**
 * Compile a nested workoutPlan tree into a flat execution queue.
 *
 * @param {PlanNode} plan  Root workoutPlan node
 * @param {{ skipLastRest?: boolean, profile?: Object, historyMap?: Object }} options
 *   `skipLastRest` (default true) applies the adjacent-recovery rest filter;
 *   `profile.training_data` is consulted for rep estimates; `historyMap` is
 *   { exerciseKey: { perRepSec, samples } } from useExerciseHistory.
 * @returns {CompiledPlan}
 */
export const compileWorkoutPlan = (
  plan,
  { skipLastRest = true, profile, historyMap } = {},
) => {
  if (!plan || !Array.isArray(plan.children) || plan.children.length === 0) {
    return { queue: [], totalDuration: 0 };
  }

  // Walk the root node directly so its own `value` is honoured when it is a
  // multi-round Group. Previously the entry point iterated `plan.children`
  // once unconditionally, so callers passing an inner Group (e.g.
  // useTotalDuration(intervalCard)) got back a single-iteration duration —
  // the source of the "X:XX | N× Y:YY math doesn't add up" bug.
  /** @type {QueueItem[]} */
  const raw = [];
  walk(plan, [], {}, buildCtx({ profile, historyMap }), raw, null);

  const queue = mergeLinkedSteps(applyRestFilter(raw, skipLastRest));

  return { queue, totalDuration: sumDurations(queue) };
};

// ---------------------------------------------------------------------------
// Linked steps (issue #177)
// ---------------------------------------------------------------------------

const unitWord = (node) => (isCalorieUnit(node.unit) ? "cal" : "");

/**
 * The title of a run of linked steps: their names joined, with each count
 * when they differ ("Squat + Press" when both are 8 reps, "Squat 8 + Press 6"
 * otherwise).
 *
 * @param {PlanNode[]} nodes
 */
export const linkedLabel = (nodes) => {
  const sameCount = nodes.every((n) => n.value === nodes[0].value && n.unit === nodes[0].unit);
  return nodes
    .map((n) => (sameCount ? n.label : `${n.label} ${n.value}${unitWord(n) ? ` ${unitWord(n)}` : ""}`))
    .join(" + ");
};

/**
 * Fold each counted step marked `linkToPrevious` into the step before it, so
 * one Done finishes the lot (#177): back-to-back sets on one bar, where
 * putting it down to tap the phone after each would break the flow.
 *
 * Only adjacent counted (reps or calories) steps from the same set of the same
 * group link; a timed step, a rest or a step from another round ends the run.
 * The merged item keeps the first step's node (so its id, load and path stay
 * the handle for edits) under the joined label, sums the estimates, and the
 * node lists every step in `linkedNodes` with its share of the estimate in
 * `linkedShares`, so the log can record each one (createPhaseLogEntries).
 *
 * @param {QueueItem[]} queue
 * @returns {QueueItem[]}
 */
export const mergeLinkedSteps = (queue) => {
  const out = [];
  const counted = (item) => item && item.executable && !item.isTimed && item.isEstimated;
  const siblings = (a, b) =>
    a.roundKey === b.roundKey &&
    a.sourceTreePath.length === b.sourceTreePath.length &&
    a.sourceTreePath.slice(0, -1).every((seg, i) => seg === b.sourceTreePath[i]);
  for (const item of queue) {
    // A changeover the generator put between the two steps doesn't belong in
    // a linked run: look back past it, and drop it when the steps link.
    let at = out.length - 1;
    while (at >= 0 && out[at].isTransition) at -= 1;
    const prev = out[at];
    if (item.node?.linkToPrevious && counted(item) && counted(prev) && siblings(prev, item)) {
      const nodes = [...(prev.node.linkedNodes || [prev.node]), item.node];
      const shares = [...(prev.node.linkedShares || [prev.durationSec]), item.durationSec];
      out.length = at + 1;
      out[at] = {
        ...prev,
        node: { ...nodes[0], label: linkedLabel(nodes), linkedNodes: nodes, linkedShares: shares },
        durationSec: prev.durationSec + item.durationSec,
      };
      continue;
    }
    out.push(item);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Round summary (issue #73)
// ---------------------------------------------------------------------------

const emptyRoundSummary = () => ({
  totalDuration: 0,
  rounds: 0,
  roundDurations: [],
  roundsVary: false,
  changeoverSec: 0,
  droppedRestSec: 0,
});

// Two rounds are "the same length" within this tolerance. Rep estimates are
// fractional (VBT concentric times run to three decimals), so an exact equality
// test would flag identical rounds as varying.
const ROUND_EQUALITY_TOLERANCE_S = 0.5;

/**
 * Per-round breakdown of a multi-round Group.
 *
 * A block header cannot honestly report `total / rounds`: rounds are rarely
 * equal. A `setScheme` ramp makes each set shorter than the last, and the rest
 * filter strips the final round's trailing rest, so the mean describes a round
 * that never runs — "3× 1:21" for a block whose sets are 1:26, 1:19 and 0:13
 * (issue #73). This returns the real figures so the UI can show a round that
 * exists and explain the difference between it and the total.
 *
 * `changeoverSec` is the injected changeover time the plan list hides
 * (issue #22) but the total still counts; `droppedRestSec` is the recovery time
 * the rest filter removed. Together they reconcile the visible rows with the
 * header: Σ(visible rows) × rounds + changeoverSec − droppedRestSec = total.
 *
 * @param {PlanNode} group  A Group node with `value` > 1 iterations
 * @param {{ profile?: Object, historyMap?: Object }} options
 *   User profile and exercise history map, for rep estimates.
 * @returns {{ totalDuration: number, rounds: number, roundDurations: number[],
 *            roundsVary: boolean, changeoverSec: number, droppedRestSec: number }}
 */
export const summariseGroupRounds = (
  group,
  { profile, historyMap } = {},
) => {
  const rounds = Number(group?.value);
  if (
    group?.type !== "Group" ||
    isTimedNode(group) || // time-capped container: `value` is a cap, not a count
    !Number.isFinite(rounds) ||
    rounds <= 1 ||
    !Array.isArray(group.children) ||
    group.children.length === 0
  ) {
    return emptyRoundSummary();
  }

  /** @type {QueueItem[]} */
  const raw = [];
  walk(group, [], {}, buildCtx({ profile, historyMap }), raw, null);
  const queue = applyRestFilter(raw, true);

  // `walk` was entered at this group, so its counter carries the key derived
  // from an empty path stack.
  const groupKey = loopKeyFor(group, []);
  const roundDurations = new Array(rounds).fill(0);
  let changeoverSec = 0;
  for (const item of queue) {
    // Every descendant carries this group's counter — `walk` tracks rounds for
    // any Group with more than one iteration, which the guard above assures.
    const iter = item.loopCounters?.[groupKey];
    if (Number.isInteger(iter) && iter >= 0 && iter < rounds) {
      roundDurations[iter] += item.durationSec;
    }
    if (isHiddenInSummary(item.node)) changeoverSec += item.durationSec;
  }

  const first = roundDurations[0];
  return {
    totalDuration: sumDurations(queue),
    rounds,
    roundDurations,
    roundsVary: roundDurations.some(
      (d) => Math.abs(d - first) > ROUND_EQUALITY_TOLERANCE_S,
    ),
    changeoverSec,
    droppedRestSec: Math.max(0, sumDurations(raw) - sumDurations(queue)),
  };
};
